import { breadcrumbSchema, webPageSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { NAP, PHONES } from "@/lib/whatsapp";
import { ogImageMeta } from "@/lib/og-image";
import { PageHero } from "@/components/site/PageHero";
import { PanelSection } from "@/components/site/PanelSection";

const TITLE = "Términos y Políticas | Residencia Oasis Cuernavaca";
const DESCRIPTION =
  "Términos de uso del sitio, aviso de privacidad y políticas de convivencia, visitas y pagos de Residencia Oasis Cuernavaca.";

/** URL preservada del sitio anterior, con la errata de slug original (sin la í). */
export const Route = createFileRoute("/terminos-y-polticas")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://oasiscuernavaca.com/terminos-y-polticas" },
      { property: "og:type", content: "website" },
      ...ogImageMeta(),
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://oasiscuernavaca.com/terminos-y-polticas" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          webPageSchema({ name: "Términos y políticas de Residencia Oasis Cuernavaca", description: DESCRIPTION, path: "/terminos-y-polticas", type: "WebPage" }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([{ name: "Términos y políticas", path: "/terminos-y-polticas" }]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        breadcrumb="Términos y políticas"
        title="Términos y Políticas"
        answer="Aquí reunimos los términos de uso, el aviso de privacidad y las políticas de visitas, convivencia, cuidados y pagos. Si algo no queda claro, escríbanos o llámenos."
      />

      <PanelSection tone="light" eyebrow="Uso del sitio" title="Términos de uso del sitio" label="Términos de uso">
        <p className="mt-3">
          La información publicada en oasiscuernavaca.com es de carácter informativo sobre nuestros
          servicios de residencia para adultos mayores. No sustituye la orientación de profesionales externos ni
          constituye un contrato de servicios; la relación contractual se formaliza por escrito al
          momento de la admisión.
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5">
          <li>Los precios y disponibilidad publicados pueden cambiar sin previo aviso.</li>
          <li>
            Los textos, fotografías y marcas de este sitio pertenecen a {NAP.name} y no pueden
            reproducirse sin autorización escrita.
          </li>
          <li>
            Los enlaces a sitios de terceros (redes sociales, mapas) se ofrecen por comodidad y no
            implican responsabilidad sobre su contenido.
          </li>
        </ul>
      </PanelSection>

      <PanelSection tone="soft" eyebrow="Privacidad" title="Aviso de privacidad" label="Aviso de privacidad">
        <p className="mt-3">
          {NAP.name}, con domicilio en {NAP.street}, {NAP.locality}, {NAP.region}, C.P.{" "}
          {NAP.postalCode}, es responsable del tratamiento de los datos personales que usted nos
          proporciona.
        </p>
        <h3 className="mt-6 font-semibold">¿Qué datos recabamos?</h3>
        <p className="mt-2">
          Nombre y datos de contacto de quien nos escribe, su plazo de interés en nuevos proyectos y la información general que
          usted decida compartir sobre su familiar (movilidad, apoyo requerido,
          memoria) cuando usa el formulario de evaluación o nos contacta por WhatsApp, teléfono o
          correo.
        </p>
        <h3 className="mt-6 font-semibold">¿Para qué los usamos?</h3>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          <li>Responder su solicitud de información y orientarle sobre la modalidad adecuada.</li>
          <li>Medir el interés y avisarle si avanza el proyecto de una residencia en Jiutepec.</li>
          <li>Agendar y dar seguimiento a visitas y procesos de admisión.</li>
          <li>Elaborar el plan de cuidados en caso de que su familiar ingrese a la residencia.</li>
        </ul>
        <h3 className="mt-6 font-semibold">Confidencialidad y terceros</h3>
        <p className="mt-2">
          No vendemos ni rentamos sus datos. La información compartida se limita al equipo de cuidado
          que participa en el acompañamiento y a las autoridades cuando la ley lo exija.
        </p>
        <h3 className="mt-6 font-semibold">Derechos ARCO</h3>
        <p className="mt-2">
          Puede solicitar el acceso, rectificación, cancelación u oposición al tratamiento de sus
          datos escribiendo a{" "}
          <a className="underline" href={`mailto:${NAP.email}`}>
            {NAP.email}
          </a>{" "}
          o llamando al{" "}
          <a className="underline" href={`tel:${PHONES[0]!.tel}`}>
            {PHONES[0]!.label}
          </a>
          . Atendemos su solicitud en un plazo máximo de 20 días hábiles.
        </p>
      </PanelSection>

      <PanelSection tone="plain" eyebrow="Políticas" title="Políticas de la residencia" label="Políticas de la residencia">
        <h3 className="mt-6 font-semibold">Visitas</h3>
        <p className="mt-2">
          Las familias pueden visitar sin horarios restrictivos. Para conocer las instalaciones por
          primera vez pedimos cita previa, para que alguien del equipo le acompañe en el recorrido.
        </p>
        <h3 className="mt-6 font-semibold">Admisión y cuidados</h3>
        <p className="mt-2">
          Antes del ingreso solicitamos una valoración de necesidades de cuidado. El plan de
          acompañamiento se acuerda con la familia y se revisa cuando cambian las necesidades del
          residente.
        </p>
        <h3 className="mt-6 font-semibold">Convivencia</h3>
        <p className="mt-2">
          Cuidamos un ambiente de hogar: respeto entre residentes, personal y familias; cuidado de
          los espacios comunes; y acuerdos claros sobre pertenencias personales y objetos de valor.
        </p>
        <h3 className="mt-6 font-semibold">Pagos y cancelaciones</h3>
        <p className="mt-2">
          La cuota se cubre por adelantado según la modalidad contratada (permanente, casa de día o
          acompañamiento post-hospitalario). Las condiciones específicas de depósito, aviso de
          salida y reembolso se detallan en el contrato de admisión que firma la familia.
        </p>
      </PanelSection>

      <PanelSection tone="light" eyebrow="Contacto" title="¿Dudas sobre estas políticas?" label="Dudas sobre estas políticas">
        <p className="mt-3">
          Escríbanos a{" "}
          <a className="underline" href={`mailto:${NAP.email}`}>
            {NAP.email}
          </a>{" "}
          o llámenos al{" "}
          <a className="underline" href={`tel:${PHONES[0]!.tel}`}>
            {PHONES[0]!.label}
          </a>
          . {NAP.street}, {NAP.locality}, {NAP.region}.
        </p>
        <p className="mt-6 rounded-xl border border-border p-4 text-sm text-muted-foreground">
          Aviso para el cliente: este documento reproduce las políticas operativas que ya aplica la
          residencia y la estructura legal mínima (términos de uso, aviso de privacidad conforme a
          la LFPDPPP y políticas de servicio). No incluye cláusulas legales redactadas por nosotros:
          antes de publicar, su abogado debe revisarlo y completar plazos, penalizaciones y
          condiciones de reembolso.
        </p>
      </PanelSection>
    </>
  );
}
